import { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// In-app evidence viewer (lightbox). Keeps the user inside TUPU (no new tab).
// images: [{ url, ... }]; meta: { name, location, datetime, user }
export function EvidenceLightbox({ images = [], index, meta, onClose, onIndex }) {
  const n = images.length;
  const prev = useCallback(() => onIndex((index - 1 + n) % n), [index, n, onIndex]);
  const next = useCallback(() => onIndex((index + 1) % n), [index, n, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && n > 1) prev();
      else if (e.key === "ArrowRight" && n > 1) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, prev, next, n]);

  if (index == null || n === 0) return null;
  const current = images[index];

  const handleBackdrop = (e) => { if (e.target === e.currentTarget) onClose(); };
  return createPortal((
    <div data-testid="evidence-lightbox" className="pointer-events-auto fixed inset-0 z-[100] flex flex-col bg-black/80 backdrop-blur-sm"
      onClick={handleBackdrop} role="dialog" aria-modal="true">
      <button data-testid="evidence-close-btn" onClick={onClose}
        className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full bg-white/90 text-[#153D35] shadow-lg hover:bg-white">
        <X className="h-5 w-5" />
      </button>

      <div className="flex flex-1 items-center justify-center p-4 sm:p-10" onClick={handleBackdrop}>
        {n > 1 && (
          <button data-testid="evidence-prev-btn" onClick={prev}
            className="mr-2 grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/90 text-[#153D35] shadow hover:bg-white">
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}
        <img data-testid="evidence-image" src={current.url} alt="Evidencia"
          className="max-h-[70vh] max-w-full rounded-lg object-contain" />
        {n > 1 && (
          <button data-testid="evidence-next-btn" onClick={next}
            className="ml-2 grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white/90 text-[#153D35] shadow hover:bg-white">
            <ChevronRight className="h-6 w-6" />
          </button>
        )}
      </div>

      <div className="bg-black/40 px-5 pb-6 pt-3 text-white" onClick={(e) => e.stopPropagation()}>
        {n > 1 && <p data-testid="evidence-counter" className="mb-1 text-xs text-white/70">{index + 1} de {n}</p>}
        {meta?.name && <p className="font-display text-base font-semibold">{meta.name}</p>}
        <p className="text-sm text-white/80">
          {[meta?.location, meta?.datetime].filter(Boolean).join(" · ")}
        </p>
        {meta?.user && <p className="text-sm text-white/70">Responsable: {meta.user}</p>}
      </div>
    </div>
  ), document.body);
}
